import { useEffect, useState } from 'react';
import { Collaborator } from '@/features/Collaborators/Models/collaborator';
import { useSalaryPermissions } from './useSalaryPermissions';
import {
  fetchCollaboratorsSalary,
  convertToLegacyFormat
} from '../Services/salarySubcollectionService';

/**
 * Hook that enriches collaborators data with salary information from subcollection
 *
 * This hook:
 * 1. Takes collaborators data (without salary fields after migration)
 * 2. Fetches salary data from subcollection (if user has permission)
 * 3. Merges salary data back into collaborators in legacy format
 * 4. Returns enriched collaborators that work with existing components
 */
export const useCollaboratorsWithSalary = (
  collaborators: Collaborator[],
  enabled: boolean = true
): {
  data: Collaborator[];
  isLoading: boolean;
  error: Error | null;
} => {
  const { canViewSalary } = useSalaryPermissions();
  const [enrichedData, setEnrichedData] = useState<Collaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Disabled by the caller, or no permission to view salary: return collaborators as-is
    if (!enabled || !canViewSalary) {
      setEnrichedData(collaborators);
      setIsLoading(false);
      return;
    }

    // If no collaborators, nothing to do
    if (!collaborators || collaborators.length === 0) {
      setEnrichedData([]);
      setIsLoading(false);
      return;
    }

    // Fetch salary data and merge
    const fetchAndMergeSalaryData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const collaboratorIds = collaborators.map(c => c.id);

        // Fetch all salary data
        const salaryDataMap = await fetchCollaboratorsSalary(collaboratorIds);

        // Merge salary data back into collaborators
        const enriched = collaborators.map(collaborator => {
          const salaryData = salaryDataMap[collaborator.id];

          if (!salaryData || !salaryData.currentEntry) {
            // No salary data found, return as-is
            return collaborator;
          }

          // Convert to legacy format and merge
          const legacyFormat = convertToLegacyFormat(salaryData);

          return {
            ...collaborator,
            ...legacyFormat
          } as Collaborator;
        });

        setEnrichedData(enriched);
      } catch (err) {
        console.error('Error fetching salary data:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch salary data'));
        // On error, return collaborators without salary data
        setEnrichedData(collaborators);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndMergeSalaryData();
  }, [collaborators, canViewSalary, enabled]);

  return {
    data: enrichedData,
    isLoading,
    error
  };
};
